import { useCallback, useMemo } from 'react';

import { ViewProps } from '../settings/interactive-response.constants';
import InteractiveDragDisplay from 'approot/activity-builder/components/shared/interactive-drag-drop/interactive-drag-display';
import {
  InteractiveResponseViewScaler,
  InteractiveResponseTitle,
} from '../../shared/interactive-response-shared';
import {
  InteractiveResponsePreviewContainer,
  InteractiveStackDragContainer,
} from '../../shared/interactive-response-shared.style';
import { initialise } from './interactive-response-sequence.utils';
import * as Styled from './interactive-response-sequence.style';
import { ComponentDimensions } from 'approot/activity-builder/components/shared/component-height/component-dimensions';
import { useActivityBuilderContext } from 'approot/activity-builder/activity-builder.context';
import { defaultDimensions } from '../../interactive-response-shared.constants';
import InteractiveSequenceDropDisplay from './interactive-sequence-drop-display';
import { useBatchComponentUpdateContext } from 'approot/activity-builder/components/shared/batch-component-update.context';

const InteractiveResponseSequencePlayPreview = ({
  id,
  properties,
  sectionId,
  shuffleItems,
  instructionHeight,
}: ViewProps & {
  shuffleItems: boolean;
  instructionHeight?: number;
}) => {
  const { activeComponent } = useActivityBuilderContext();
  const { updateComponentProperties } = useBatchComponentUpdateContext();

  const { dragItems, dropItems } = useMemo(() => {
    return initialise(
      properties.interactiveResponse.variant,
      properties.interactiveResponse.items,
      shuffleItems
    );
  }, [properties.interactiveResponse, shuffleItems]);

  const handleHeightUpdate = useCallback(
    (width: number, height: number) => {
      const dimensionData = {
        __typename: 'ComponentDimension',
        width,
        height: Math.floor(height),
      };
      updateComponentProperties({
        input: {
          sectionId,
          componentId: id,
          componentData: {
            properties: {
              dimensionData,
            },
          },
        },
        properties: {
          ...properties,
          // @ts-ignore responseDimensionData is an alias TS cant understand
          dimensionData: {
            ...dimensionData,
            __typename: 'ComponentDimension',
          },
        },
        typename: 'ActivitiesInteractiveResponseComponent',
      });
    },
    [id, properties, sectionId, updateComponentProperties]
  );

  return (
    <ComponentDimensions
      id={id}
      showHandle={activeComponent?.id === id}
      extraHeight={instructionHeight}
      defaultValue={properties.responseDimensionData || defaultDimensions}
      onResizeEnd={handleHeightUpdate}
    >
      <InteractiveResponseViewScaler>
        <InteractiveResponseTitle
          title={properties.interactiveResponse.title}
          subTitle={properties.interactiveResponse.subTitle}
        />

        <InteractiveResponsePreviewContainer data-selector="interactive-response-preview-container">
          {dragItems.map((dragItem, index) => (
            <InteractiveStackDragContainer
              key={`${dragItem.id}-${index}`}
              $index={index}
              data-selector={`interactive-response-sequence-container-item-${index}`}
            >
              {!dragItem.isDropped && (
                <InteractiveDragDisplay
                  imageUrl={dragItem.question.image?.small}
                  isFit={dragItem.question.image?.isFit}
                  description={dragItem.question.image?.description || ''}
                  baseColour={dragItem.question.baseColour}
                  variant={dragItem.question.variant}
                  text={dragItem.question.text}
                  richtext={dragItem.question.richtext}
                />
              )}
            </InteractiveStackDragContainer>
          ))}
        </InteractiveResponsePreviewContainer>

        <Styled.InteractiveResponsePreviewDropItemContainer data-selector="interactive-response-sequence-drop-items">
          {dropItems.map((dropItem, index) => (
            <InteractiveSequenceDropDisplay
              key={`${dropItem.id}`}
              label={dropItem.label}
              labelLength={properties.interactiveResponse.labelLength}
              labelBorder={properties.interactiveResponse.labelBorder}
              data-selector={`interactive-response-sequence-drop-item-${index}`}
            />
          ))}
        </Styled.InteractiveResponsePreviewDropItemContainer>
      </InteractiveResponseViewScaler>
    </ComponentDimensions>
  );
};

export default InteractiveResponseSequencePlayPreview;
